import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useKeycloak } from "@react-keycloak/web";
import useProjectStore from '../../../hooks/useProjectStore';

import Swal from "sweetalert2";
import projectService from "../../../services/Projects/ProjectService";

import { FaRegUserCircle } from "react-icons/fa";

import Tag from '../../Atoms/Tag';
import Input from '../../Atoms/Input';
import TextArea from '../../Atoms/TextArea';
import Subtitle from '../../Atoms/Subtitle';
import Loader from "../../Atoms/Loader";

import Accordion from '../../Molecules/Accordion';
import Dropdown from '../../Molecules/Dropdown';
import DatePicker from '../../Molecules/DatePicker';
import CompanyComboBox from '../../Molecules/CompanyComboBox';
import InputTags from '../../Molecules/InputTags';
import MembersAvatars from '../../Molecules/MembersAvatars';

import InviteMembersModal from '../../Parts/InviteMembersModal';

import { projectTypesPT } from '../../../utils/projectTypes';


export default function ReviewProjectTemplate({ companies }) {
    const { keycloak } = useKeycloak();

    const navigate = useNavigate();
    
    const [inviteMembersModal, setInviteMembersModal] = useState(false);
    const [newProjectId, setNewProjectId] = useState(null);
    const [loading, setLoading] = useState(false);

    const { 
        projectDataStore,
        projectSummary,
        updateCompany,
        updateFocalPoint,
        updateFocalPointPosition,
        updateFocalPointEmail,
        updateBusinessDescritive,
        updateProjectTitle,
        updateProjectType,
        updateStartDate,
        updateEndDate,
        updateGoal,
        updateBusinessContext,
        updateTargetPublic,
        updateExpectedResults,
        updateImpactedBusinessIndicators,
        updateDeliverables,
        isFormValid,
        resetProject
    } = useProjectStore();

    const handleSubmit = async () => {
        try {
            setLoading(true);
            if (isFormValid()) {
                const payload = {
                    projectDataStore,
                    projectSummary
                }

                const newProject = await projectService.createProject(payload, keycloak);
                Swal.fire({
                    position: "top-end",
                    title: "Projeto criado com sucesso",
                    showConfirmButton: false,
                    timer: 2000,
                    background: '#27272A',
                    color: '#fff',
                });
                setNewProjectId(newProject.id);
                setInviteMembersModal(true);
            } else {
                Swal.fire({ 
                    position: "top-end",
                    title: "Por favor, preencha todos os campos obrigatórios",
                    showConfirmButton: false,
                    timer: 1800,
                    background: '#27272A',
                    color: '#fff',
                });
                return false
            }
        } catch (error) {
            Swal.fire({
                position: "top-end",
                title: "Erro ao processar projeto",
                showConfirmButton: false,
                timer: 1800,
                background: '#27272A',
                color: '#fff',
            });
        } finally {
            setLoading(false);
        }
        
    }

    const handleCloseInviteMembersModal = () => {
        setInviteMembersModal(false);
        resetProject();
        navigate('/projects');
    }

    const handleCancel = () => {
        Swal.fire({
            position: "top-end",
            title: "Rascunho excluído com sucesso",
            showConfirmButton: false,
            timer: 1800,
            background: '#27272A',
            color: '#fff',
        });

        resetProject();
        navigate('/projects');
    }

    const isFormDisabled = () => {
        const { customerData, projectData } = projectDataStore;

        const isCompanyValid = customerData.company && 
            typeof customerData.company === 'object' && 
            customerData.company.id && 
            customerData.company.name && 
            customerData.company.sector;

        const mandatoryFields = {
            company: isCompanyValid,
            businessDescritive: customerData.businessDescritive,
            title: projectData.title,
            type: projectData.type,
            startDate: projectData.startDate,
            goal: projectData.goal,
            targetPublic: projectData.targetPublic,
            projectSummary: projectSummary
        };

        return Object.values(mandatoryFields).some(field => 
            field === null || field === '' || field.trim() === '' || field === false
        );
    }

    const buttonStyles = {
        enabled: {
            backgroundColor: '#8E51FF',
            fontWeight: 'bold',
            padding: '10px 20px',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
        },
        disabled: {
            backgroundColor: '#6B7280',
            fontWeight: 'bold',
            padding: '10px 20px',
            color: '#9CA3AF',
            border: 'none',
            borderRadius: '6px',
            cursor: 'not-allowed'
        }
    };

    return (
        <>
            <InviteMembersModal
                isOpen={inviteMembersModal}
                onClose={handleCloseInviteMembersModal}
                projectId={newProjectId}
            />

            <div className="container-xxl my-5">
                <div id="edit-project-header" className="d-flex flex-row justify-content-between">
                    <h2>Revisar {projectDataStore.projectData.title}</h2>

                    <button 
                        className='btn d-flex flex-row align-items-center gap-2' 
                        style={{
                            cursor: projectDataStore.projectData.members?.length > 0 ? 'pointer' : 'not-allowed',
                            color: projectDataStore.projectData.members?.length > 0 ? '#1F2A3A' : '#9F9FA9',
                        }}
                    >
                        <FaRegUserCircle />
                        Editar membros
                    </button>
                </div>

                <div>
                    <div className="d-flex flex-row align-items-center gap-2 mb-3">
                        <Tag text={
                            typeof projectDataStore.customerData.company === 'object' 
                                ? projectDataStore.customerData.company?.name 
                                : projectDataStore.customerData.company
                        } />

                        {projectDataStore.projectData.members?.length > 0 && (
                            <MembersAvatars members={projectDataStore.projectData.members} />
                        )}

                        {projectDataStore.projectData.last_user_updated?.date && (
                            <p>{projectDataStore.projectData.last_user_updated.date}</p>
                        )}

                        {projectDataStore.projectData.last_user_updated?.user && (
                            <p>Editado por {projectDataStore.projectData.last_user_updated.user}</p>
                        )}

                        {projectDataStore.projectData.last_user_updated?.time_ago && (
                            <p className="text-secondary">{projectDataStore.projectData.last_user_updated.time_ago}</p>
                        )}
                    </div>

                    <div id="edit-project-description" className="mb-5">
                        <Subtitle>Defina as informações do seu projeto para obter soluções personalizadas</Subtitle>
                    </div>

                    <div id="edit-project-form">         
                        <Accordion title="Dados do cliente">
                            <div className='row' >
                                <div className='col-4'>
                                    <CompanyComboBox
                                        label="Empresa"
                                        placeholder="Selecione a empresa"
                                        mandatory={true}
                                        items={companies}
                                        onSelect={updateCompany}
                                        selectedCompanyId={projectDataStore.customerData.company?.id}
                                    />
                                </div>

                                <div className='col-8'>
                                    <Input
                                        label="Nome do ponto focal"
                                        placeholder="Digite o nome e sobrenome"
                                        mandatory={false}
                                        value={projectDataStore.customerData.focalPoint}
                                        onChange={(e) => updateFocalPoint(e.target.value)}
                                    />
                                </div>

                                <div className='col-8'>
                                    <Input
                                        label="Cargo e área do ponto focal"
                                        placeholder="Digite o cargo e a área (Ex: Gerente de TI)"
                                        mandatory={false}
                                        value={projectDataStore.customerData.focalPointPosition}
                                        onChange={(e) => updateFocalPointPosition(e.target.value)}
                                    />
                                </div>

                                <div className='col-4'>
                                    <Input
                                        label="E-mail do ponto focal"
                                        placeholder="Digite o e-mail"
                                        mandatory={false}
                                        value={projectDataStore.customerData.focalPointEmail}
                                        onChange={(e) => updateFocalPointEmail(e.target.value)}
                                    />
                                </div>

                                <div className='col-12'>
                                    <TextArea 
                                        label="Descritivo do negócio"
                                        placeholder="Descreva o negócio do cliente e principais produtos"
                                        mandatory={true}
                                        tooltipText="Exemplo: Grande empresa de energia com atuação nacional que possui 2 principais produtos, Geração Distribuida Compartilhada (GDC) e Mercado Livre"
                                        style={{ minHeight: '300px' }}
                                        value={projectDataStore.customerData.businessDescritive}
                                        onChange={(e) => updateBusinessDescritive(e.target.value)}
                                    />
                                </div>
                            </div>
                        </Accordion>

                        <Accordion title="Detalhes do projeto">
                            <div className='row'>
                                <div className='col-12'>
                                    <Input
                                        label="Título do projeto"
                                        placeholder="Digite o título do projeto"
                                        mandatory={true}
                                        value={projectDataStore.projectData.title}
                                        onChange={(e) => updateProjectTitle(e.target.value)}
                                    />
                                </div>

                                <div className='col-8'>
                                    <Dropdown
                                        label="Tipo de projeto"
                                        items={projectTypesPT}
                                        onSelect={updateProjectType}
                                        mandatory={true}
                                    />
                                </div>

                                <div className='col-2'>
                                    <DatePicker 
                                        label='Início do projeto' 
                                        mandatory={true} 
                                        value={projectDataStore.projectData.startDate}
                                        onChange={updateStartDate}
                                    />
                                </div>

                                <div className='col-2'>
                                    <DatePicker 
                                        label='Fim do projeto' 
                                        mandatory={false} 
                                        value={projectDataStore.projectData.endDate}
                                        onChange={updateEndDate}
                                    />
                                </div>

                                <div className='col-12'>
                                    <TextArea 
                                        label="Objetivo"
                                        placeholder="Descreva o objetivo do projeto."
                                        mandatory={true}
                                        tooltipText="Por exemplo: Conduzir pesquisa de mercado para identificar oportunidades e estratégias que aumentem a conversão e retenção de clientes PJ e PF no mercado de energia. "
                                        style={{ minHeight: '300px' }}
                                        value={projectDataStore.projectData.goal}
                                        onChange={(e) => updateGoal(e.target.value)}
                                    />
                                </div>

                                <div className='col-12'>
                                    <TextArea 
                                        label="Contexto do negócio"
                                        placeholder="Descreva o contexto do negócio, qual o cenário da empresa que originou o desafio do projeto"
                                        mandatory={false}
                                        tooltipText="Por exemplo: Em um cenário de transição e aumento da concorrência no setor de energia, a empresa perdeu market share em seus principais produtos. A ausência de uma cultura centrada no cliente agrava o desafio frente a novos players de perfil varejista. Portanto, o desafio é aprofundar o conhecimento sobre os clientes para desenvolver estratégias de canais e produtos mais eficazes, viabilizando a expansão da empresa neste novo mercado."
                                        style={{ minHeight: '300px' }}
                                        value={projectDataStore.projectData.businessContext}
                                        onChange={(e) => updateBusinessContext(e.target.value)}
                                    />
                                </div>

                                <div className='col-12'>
                                    <TextArea 
                                        label="Público alvo"
                                        placeholder="Descreva o público alvo do projeto"
                                        mandatory={true}
                                        tooltipText="Exemplo: Empresas que já migraram para o Mercado Livre de energia, Consumidores de conteúdo de reality shows, Time de TI da empresa, etc."
                                        style={{ minHeight: '300px' }}
                                        value={projectDataStore.projectData.targetPublic}
                                        onChange={(e) => updateTargetPublic(e.target.value)}
                                    />
                                </div>
                            </div>
                        </Accordion>

                        <Accordion title="Impacto">
                            <div className='row'>
                                <div className='col-12'>
                                    <TextArea 
                                        label="Resultados esperados"
                                        placeholder="Descreva o que a empresa espera como resultado do projeto"
                                        mandatory={false}
                                        tooltipText="Exemplo: Expandir presença e reconhecimento de marca, Reduzir o Custo de Aquisição de Clientes (CAC), Redução do churn atual."
                                        style={{ minHeight: '300px' }}
                                        value={projectDataStore.impact.expectedResults}
                                        onChange={(e) => updateExpectedResults(e.target.value)}
                                    />
                                </div>

                                <div className='col-12'>
                                    <InputTags
                                        label="Indicadores de negócio impactados"
                                        placeholder="Digite e pressione 'Enter' para adicionar novas tags."
                                        tooltipText="Exemplo: Custo de Aquisição (CAC), Churn Rate, NPS, Market Share, etc."
                                        value={projectDataStore.impact.impactedBusinessIndicators}
                                        onChange={updateImpactedBusinessIndicators}
                                    />
                                </div>

                                <div className='col-12'>
                                    <InputTags
                                        label="Entregáveis"
                                        placeholder="Digite e pressione 'Enter' para adicionar novas tags"
                                        mandatory={false}
                                        tooltipText="Exemplo: Jornada AS IS, Perfis, Benchmark, Direcionamentos estratégicos, Cardápio de ideias, etc."
                                        value={projectDataStore.impact.deliverables}
                                        onChange={updateDeliverables}
                                    />
                                </div>
                            </div>
                        </Accordion>

                        <div className='w-auto d-flex flex-row justify-content-end gap-3'>
                            <button
                                className='btn'
                                onClick={handleCancel}
                                style={{ 
                                    backgroundColor: 'transparent', 
                                    border: '1px solid #314158',
                                    color: '#fff',
                                    padding: '10px 20px',
                                    borderRadius: '6px'
                                }}
                            >
                                Descartar rascunho
                            </button>

                            {loading ? (
                                <button className='btn'>
                                    <Loader />
                                </button>
                            ): (
                                <button
                                    className='btn'
                                    onClick={handleSubmit}
                                    style={isFormDisabled() ? buttonStyles.disabled : buttonStyles.enabled}
                                    disabled={isFormDisabled()}
                                >
                                    Salvar e continuar
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}